import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyButtonHandle } from "../components/KeyButton";
import {
  ALPHABET,
  CONTAINER_SIZE,
  INVALID_SHAKE_DURATION_MS,
  KEY_ANGLE_OFFSET,
  ORBIT_RADIUS,
  STROKE_DURATION_MS,
  WIN_DELAY_MS,
  WIN_PRE_SHAKE_MS,
  WIN_SHAKE_DURATION_MS,
} from "../constants";
import type { GameCallbacks, Position } from "../types";
import { pickRandomKeys, polarToCartesian } from "../utils";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import missClickSound from "../assets/audio/miss-click.wav";
import paintingSound from "../assets/audio/painting.wav";
import winSound from "../assets/audio/win.wav";
import clickSound from "../assets/audio/click.wav";

export interface GameState {
  gameKeys: string[];
  pressedKeys: Set<string>;
  progress: number;
  won: boolean;
  stroking: boolean;
  shaking: boolean;
  invalid: boolean;
  started: boolean;
  keyPositions: Position[];
  keyRefs: React.RefObject<Record<string, KeyButtonHandle>>;
}

export interface GameActions {
  startGame: () => void;
  resetGame: () => void;
  handleKeyPress: (key: string) => void;
}

export function useGameLogic(
  callbacks: GameCallbacks,
  config: { numberOfKeys: number; allowedKeys: string[] },
): GameState & GameActions {
  const {
    onWin,
    onValidKeyPress,
    onInvalidKeyPress,
    onGameStart,
    onGameReset,
  } = callbacks;
  const { numberOfKeys, allowedKeys } = config;

  const [gameKeys, setGameKeys] = useState<string[]>([]);
  const [pressedKeys, setPressedKeys] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState(0);
  const [won, setWon] = useState(false);
  const [stroking, setStroking] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [started, setStarted] = useState(false);
  const keyRefs = useRef<Record<string, KeyButtonHandle>>({});
  const { playEffect, stopEffect } = useAudioEffects();

  const initGame = useCallback(
    (isReset: boolean) => {
      const pool = allowedKeys.length > 0 ? allowedKeys : ALPHABET;
      const keys = pickRandomKeys(numberOfKeys, pool);
      setGameKeys(keys);
      setPressedKeys(new Set());
      setProgress(0);
      setWon(false);
      setShaking(false);
      setInvalid(false);
      setStarted(true);
      keyRefs.current = {};
      if (isReset) onGameReset?.();
      else onGameStart?.();
    },
    [numberOfKeys, allowedKeys, onGameStart, onGameReset],
  );

  const startGame = useCallback(() => {
    initGame(false);
    playEffect(clickSound);
  }, [initGame]);
  const resetGame = useCallback(() => {
    initGame(true);
    playEffect(clickSound);
    stopEffect({ inBackground: true });
  }, [initGame]);

  useEffect(() => {
    startGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyPress = useCallback(
    (key: string) => {
      if (!started || won) return;
      const upper = key.toUpperCase();

      if (!gameKeys.includes(upper) || pressedKeys.has(upper)) {
        setInvalid(true);
        setTimeout(() => setInvalid(false), INVALID_SHAKE_DURATION_MS);
        onInvalidKeyPress?.(upper);
        playEffect(missClickSound);

        return;
      }

      const ref = keyRefs.current[upper];
      if (ref?._pop) ref._pop();

      const newPressed = new Set(pressedKeys);
      newPressed.add(upper);
      setPressedKeys(newPressed);

      const newProgress = Math.round((newPressed.size / gameKeys.length) * 100);
      setProgress(newProgress);

      playEffect(paintingSound);
      setStroking(true);
      setTimeout(() => setStroking(false), STROKE_DURATION_MS);

      onValidKeyPress?.(upper);

      if (newPressed.size === gameKeys.length) {
        setTimeout(() => {
          setShaking(true);
          setTimeout(() => setShaking(false), WIN_SHAKE_DURATION_MS);
          setTimeout(() => {
            setWon(true);
            playEffect(winSound, { inBackground: true });
            onWin?.();
          }, WIN_DELAY_MS);
        }, WIN_PRE_SHAKE_MS);
      }
    },
    [
      started,
      won,
      gameKeys,
      pressedKeys,
      onInvalidKeyPress,
      onValidKeyPress,
      onWin,
    ],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.repeat) return;
      handleKeyPress(e.key);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleKeyPress]);

  const keyPositions = gameKeys.map((_, i) => {
    const angle = (i / gameKeys.length) * 360 + KEY_ANGLE_OFFSET;
    return polarToCartesian(
      CONTAINER_SIZE / 2,
      CONTAINER_SIZE / 2,
      ORBIT_RADIUS,
      angle,
    );
  });

  return {
    gameKeys,
    pressedKeys,
    progress,
    won,
    stroking,
    shaking,
    invalid,
    started,
    keyPositions,
    keyRefs,
    startGame,
    resetGame,
    handleKeyPress,
  };
}
